import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import dayjs from 'dayjs';
import { instance } from '~/utils/api/api';
import { Spinner } from '~/ui';
import { getLocalTimeDate } from '~/utils/getLocalTimeDate';
import styles from './TicketList.module.scss';

interface TicketListItem {
  id: number;
  name: string;
  description: string;
  review: string | null;
  created_at: string;
  ticket_photo: unknown[];
  ticket_video: unknown[];
  category: { id: number; category: string } | null;
  statuses: { id: number; name: string } | null;
  comments_count: number;
  last_comment: {
    id: number;
    description: string;
    created_at: string;
    user_id: number;
    my_comment: boolean;
    time: string;
  } | null;
}

interface TicketListProps {
  selectedTicketId: number | null;
  onSelect: (ticketId: number | null) => void;
  search: string;
  onSearchChange: (value: string) => void;
  categoryId: number | null;
  onCategoryChange: (value: number | null) => void;
}

const CATEGORIES = [
  { id: 1, key: 'category_food' },
  { id: 2, key: 'category_accommodation' },
  { id: 3, key: 'category_services' },
  { id: 4, key: 'category_free_form' },
];

export const TicketList = ({
  selectedTicketId,
  onSelect,
  search,
  onSearchChange,
  categoryId,
  onCategoryChange,
}: TicketListProps) => {
  const { userID } = useParams();
  const { t } = useTranslation();
  const [debouncedSearch, setDebouncedSearch] = useState(search);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryFn: async () => {
      const params: Record<string, unknown> = {};
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (categoryId) params.category_tickets_id = categoryId;

      const response = await instance.get(
        `admin/users/${userID}/chat/tickets`,
        { params }
      );
      return response.data;
    },
    queryKey: ['user-chat-tickets', userID, debouncedSearch, categoryId],
    refetchInterval: 30000,
  });

  const tickets: TicketListItem[] = data?.tickets ?? [];

  return (
    <div className={styles.container}>
      {/* Поиск */}
      <div className={styles.searchBox}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder={t('chat_search_placeholder')}
          value={search}
          onChange={e => onSearchChange(e.target.value)}
        />
      </div>

      {/* Фильтр по категориям */}
      <div className={styles.filterRow}>
        <button
          className={cn(styles.filterChip, { [styles.filterChipActive]: categoryId === null })}
          onClick={() => onCategoryChange(null)}
        >
          {t('all')}
        </button>
        {CATEGORIES.map(cat => (
          <button
            key={cat.id}
            className={cn(styles.filterChip, { [styles.filterChipActive]: categoryId === cat.id })}
            onClick={() => onCategoryChange(cat.id)}
          >
            {t(cat.key)}
          </button>
        ))}
      </div>

      {/* "Все заявки" */}
      <div
        className={cn(styles.allTicketsItem, {
          [styles.active]: selectedTicketId === null,
        })}
        onClick={() => onSelect(null)}
      >
        {t('all_requests')}
      </div>

      {isLoading && (
        <div className={styles.loader}>
          <Spinner />
        </div>
      )}

      {!isLoading && tickets.length === 0 && (
        <div className={styles.empty}>{t('no_requests')}</div>
      )}

      {tickets.map(ticket => (
        <div
          key={ticket.id}
          className={cn(styles.item, {
            [styles.active]: selectedTicketId === ticket.id,
          })}
          onClick={() => onSelect(ticket.id)}
        >
          <div className={styles.ticketName}>
            #{ticket.id} {t('request_dated')} {dayjs(ticket.created_at).format('DD.MM.YYYY')}
          </div>
          {ticket.category && (
            <div className={styles.category}>{ticket.category.category}</div>
          )}
          {ticket.last_comment ? (
            <div className={styles.lastMessage}>
              {ticket.last_comment.description?.slice(0, 50) || '📷 Фото'}
            </div>
          ) : (
            <div className={styles.lastMessage}>
              {ticket.description?.slice(0, 50) || '—'}
            </div>
          )}
          <div className={styles.meta}>
            <span className={styles.date}>
              {getLocalTimeDate(
                ticket.last_comment?.created_at || ticket.created_at
              )}
            </span>
            {ticket.comments_count > 0 && (
              <span className={styles.commentsCount}>
                {ticket.comments_count}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
